import { ANALYTICS_SOURCES } from '../../constants';
import { ROUTES as WEB_ROUTES } from '../../constants/routes';
import { ROUTES as MOBILE_ROUTES } from '../../constants/routes.native';

/**
 * Maps routes to analytics sources.
 * Supports both web paths and mobile screen names.
 *
 * For routes with sourceId, use getContext to extract it from params:
 * - Web: params come from URL path segments (e.g., /knowledge/123 → { id: '123' })
 * - Mobile: params come from React Navigation (e.g., navigate('Knowledge', { id: '123' }))
 */
export const ROUTE_SOURCE_CONFIG = {
  // Web routes
  [WEB_ROUTES.FAVORITES]: { source: ANALYTICS_SOURCES.FAVORITES },
  [WEB_ROUTES.MY_CONTENT]: { source: ANALYTICS_SOURCES.MY_CONTENT },
  [WEB_ROUTES.CONTACTS]: { source: ANALYTICS_SOURCES.CONTACTS },
  [WEB_ROUTES.SEARCH]: { source: ANALYTICS_SOURCES.SEARCH },
  [WEB_ROUTES.KNOWLEDGE_HUB]: {
    source: ANALYTICS_SOURCES.KNOWLEDGE_HUB,
    getContext: params => ({ sourceId: params['hub-category'] }),
  },
  [WEB_ROUTES.COMMUNICATION_CENTER]: { source: ANALYTICS_SOURCES.COMMUNICATION_CENTER },
  [WEB_ROUTES.COMMUNICATION_ITEM]: {
    source: ANALYTICS_SOURCES.COMMUNICATION_CENTER,
    getContext: params => ({ sourceId: params.id }),
  },
  [WEB_ROUTES.QUALITY_METRIC]: { source: ANALYTICS_SOURCES.QUALITY_METRICS },
  [WEB_ROUTES.HOME]: { source: ANALYTICS_SOURCES.DASHBOARD },
  [WEB_ROUTES.SELECT_ORGANIZATION]: { source: ANALYTICS_SOURCES.ORGANIZATION_SELECTOR },
  [WEB_ROUTES.ROTATIONS]: {
    source: ANALYTICS_SOURCES.ROTATIONS,
    getContext: params => ({ sourceId: params.id }),
  },
  [WEB_ROUTES.KNOWLEDGE]: {
    source: ANALYTICS_SOURCES.KNOWLEDGE_VIEWER,
    getContext: params => ({ sourceId: params.category }),
  },
  [WEB_ROUTES.LABS]: {
    source: ANALYTICS_SOURCES.LAB,
    getContext: params => ({ sourceId: params.values }),
  },
  [WEB_ROUTES.ADMISSION]: {
    source: ANALYTICS_SOURCES.CHECKLISTS,
    // Root level - no directoryId
  },
  [WEB_ROUTES.ADMISSION_CATEGORIES]: {
    source: ANALYTICS_SOURCES.CHECKLISTS,
    // Directory level - extract directoryId from path
    getContext: params => ({ sourceId: params.id }),
  },
  [WEB_ROUTES.SETTINGS_ORGANIZATIONAL_STRUCTURE]: {
    source: ANALYTICS_SOURCES.ORGANIZATIONAL_STRUCTURE,
  },
  [WEB_ROUTES.CHANGELOG]: { source: ANALYTICS_SOURCES.CHANGELOG },

  // Mobile routes
  [MOBILE_ROUTES.FAVORITES]: { source: ANALYTICS_SOURCES.FAVORITES },
  [MOBILE_ROUTES.TAB_FAVORITES]: { source: ANALYTICS_SOURCES.FAVORITES },
  [MOBILE_ROUTES.CONTACTS]: { source: ANALYTICS_SOURCES.CONTACTS },
  [MOBILE_ROUTES.SEARCH]: { source: ANALYTICS_SOURCES.SEARCH },
  [MOBILE_ROUTES.ASK_PANDA]: { source: ANALYTICS_SOURCES.AI_CHAT },
  [MOBILE_ROUTES.HOME]: { source: ANALYTICS_SOURCES.DASHBOARD },
  [MOBILE_ROUTES.ORGANIZATION_SELECTOR]: { source: ANALYTICS_SOURCES.ORGANIZATION_SELECTOR },
  [MOBILE_ROUTES.QUALITY_METRIC]: { source: ANALYTICS_SOURCES.QUALITY_METRICS },
  [MOBILE_ROUTES.KNOWLEDGE]: {
    source: ANALYTICS_SOURCES.KNOWLEDGE_VIEWER,
    getContext: params => ({ sourceId: params.id }),
  },
  [MOBILE_ROUTES.LABS]: { source: ANALYTICS_SOURCES.LAB },
  [MOBILE_ROUTES.LABS_CATEGORY]: {
    source: ANALYTICS_SOURCES.LAB,
    getContext: params => ({ sourceId: params.id }),
  },
  [MOBILE_ROUTES.ROTATION_ITEM]: {
    source: ANALYTICS_SOURCES.ROTATIONS,
    getContext: params => ({ sourceId: params.id }),
  },
  [MOBILE_ROUTES.CHECKLISTS]: { source: ANALYTICS_SOURCES.CHECKLISTS },
  [MOBILE_ROUTES.CHECKLISTS_CATEGORY]: {
    source: ANALYTICS_SOURCES.CHECKLISTS,
    getContext: params => ({ sourceId: params.id }),
  },
  [MOBILE_ROUTES.NOTIFICATIONS]: { source: ANALYTICS_SOURCES.COMMUNICATION_CENTER },
  [MOBILE_ROUTES.NOTIFICATION_ITEM]: {
    source: ANALYTICS_SOURCES.COMMUNICATION_CENTER,
    getContext: params => ({ sourceId: params.communicationId }),
  },
};

/**
 * Get source context from current route
 *
 * @param {string} route - Current route (pathname for web, screen name for mobile)
 * @param {object} params - Route params (from URL for web, navigation params for mobile)
 * @returns {object|null} - { source, sourceId?, ... } or null if no match
 *
 * @example
 * // Web
 * getSourceFromRoute('/knowledge/123', {})
 * // → { source: 'knowledge-viewer', sourceId: '123' }
 *
 * @example
 * // Mobile
 * getSourceFromRoute('Knowledge', { id: '123' })
 * // → { source: 'knowledge-viewer', sourceId: '123' }
 */
export function getSourceFromRoute(route, params = {}) {
  // Try exact match first
  const config = ROUTE_SOURCE_CONFIG[route];
  if (config) {
    const context = config.getContext?.(params) || {};
    return { source: config.source, ...context };
  }

  // Try pattern matching for web routes with params
  // Only iterate through routes that contain ':' (parameterized routes)
  for (const [pattern, config] of Object.entries(ROUTE_SOURCE_CONFIG)) {
    if (!pattern.includes(':')) continue; // Skip non-parameterized routes

    // Convert pattern to regex: /knowledge/:id → /knowledge/([^/]+)
    const regex = new RegExp('^' + pattern.replace(/:[^/]+/g, '([^/]+)') + '$');
    const match = route.match(regex);

    if (match) {
      // Extract param names: [':id', ':categoryId'] → ['id', 'categoryId']
      const paramNames = (pattern.match(/:[^/]+/g) || []).map(p => p.slice(1));

      // Build params object from regex captures
      const extractedParams = { ...params };
      paramNames.forEach((name, i) => {
        extractedParams[name] = match[i + 1];
      });

      const context = config.getContext?.(extractedParams) || {};
      return { source: config.source, ...context };
    }
  }

  return null;
}
